import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { storesApi } from '@/services/api';
import { SeoMeta } from '@/components/seo/SeoMeta';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Store, MapPin, Phone, Clock, ChevronRight, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icon
const storeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to recenter map
const RecenterMap = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 10);
    }
  }, [center, map]);
  return null;
};

// Swedish coordinates for demo stores (actual geocoding would require API)
const STORE_COORDINATES = {
  'Stockholm': { lat: 59.3293, lng: 18.0686 },
  'Göteborg': { lat: 57.7089, lng: 11.9746 },
  'Malmö': { lat: 55.6050, lng: 13.0038 },
  'Uppsala': { lat: 59.8586, lng: 17.6389 },
  'Solna': { lat: 59.3640, lng: 18.0050 },
};

export const MapPage = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStore, setSelectedStore] = useState(null);
  const [mapCenter, setMapCenter] = useState([59.3293, 18.0686]); // Default: Stockholm

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await storesApi.getAll();
        const storesData = response.data.stores || response.data;
        
        // Add coordinates to stores
        const storesWithCoords = storesData.map(store => {
          // Try to match by county/location
          const coordKey = Object.keys(STORE_COORDINATES).find(key => 
            store.location?.includes(key) || store.county?.includes(key)
          );
          const coords = coordKey ? STORE_COORDINATES[coordKey] : STORE_COORDINATES['Stockholm'];
          
          return {
            ...store,
            coordinates: coords
          };
        });
        
        setStores(storesWithCoords);
      } catch (error) {
        console.error('Error fetching stores:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStores();
  }, []);

  const filteredStores = stores.filter(store => 
    store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.county?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleStoreSelect = (store) => {
    setSelectedStore(store);
    if (store.coordinates) {
      setMapCenter([store.coordinates.lat, store.coordinates.lng]);
    }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="map-page">
      <SeoMeta
        title="Hitta byggvaruhus på karta"
        description="Se var ByggFynds anslutna byggvaruhus finns i Sverige. Sök butik, ort eller län på kartan."
        canonicalPath="/karta"
      />
      <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)]">
        {/* Sidebar */}
        <div className="w-full lg:w-96 bg-white shadow-md flex flex-col">
          {/* Search Header */}
          <div className="p-4 border-b">
            <h1 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#FF8A00]" />
              Hitta byggvaruhus
            </h1>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Sök butik, ort eller län..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Store List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-24 bg-gray-100 rounded animate-pulse" />
                ))}
              </div>
            ) : filteredStores.length > 0 ? (
              <div className="divide-y">
                {filteredStores.map((store) => (
                  <div
                    key={store.id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedStore?.id === store.id ? 'bg-blue-50 border-l-4 border-[#003B73]' : ''
                    }`}
                    onClick={() => handleStoreSelect(store)}
                    data-testid={`map-store-${store.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {store.logo_url ? (
                        <div className="w-12 h-12 rounded bg-gray-100 overflow-hidden flex-shrink-0">
                          <img src={store.logo_url} alt="" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded bg-[#003B73]/10 flex items-center justify-center flex-shrink-0">
                          <Store className="h-6 w-6 text-[#003B73]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#0F172A] truncate">{store.name}</h3>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {store.location}, {store.county}
                        </p>
                        {store.contact_phone && (
                          <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                            <Phone className="h-3 w-3" />
                            {store.contact_phone}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {selectedStore?.id === store.id && (
                      <div className="mt-3 pt-3 border-t">
                        {store.opening_hours && (
                          <div className="text-sm mb-3">
                            <p className="font-medium text-gray-700 flex items-center gap-1 mb-1">
                              <Clock className="h-4 w-4" />
                              Öppettider
                            </p>
                            <div className="text-gray-600 space-y-0.5">
                              {Object.entries(store.opening_hours).map(([day, hours]) => (
                                <p key={day} className="flex justify-between">
                                  <span className="capitalize">{day}:</span>
                                  <span>{hours}</span>
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                        <Link to={`/butik/${store.id}`}>
                          <Button className="w-full bg-[#FF8A00] hover:bg-[#e67c00]">
                            Visa butik <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Store className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Inga butiker hittades</p>
              </div>
            )}
          </div>
        </div>

        {/* Map */}
        <div className="flex-1 h-[400px] lg:h-full">
          <MapContainer
            center={mapCenter}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <RecenterMap center={mapCenter} />
            
            {filteredStores.map((store) => (
              store.coordinates && (
                <Marker
                  key={store.id}
                  position={[store.coordinates.lat, store.coordinates.lng]}
                  icon={storeIcon}
                  eventHandlers={{
                    click: () => handleStoreSelect(store),
                  }}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <h3 className="font-bold text-[#003B73]">{store.name}</h3>
                      <p className="text-sm text-gray-600">{store.location}, {store.county}</p>
                      {store.contact_phone && (
                        <p className="text-sm text-gray-600 mt-1">
                          <Phone className="h-3 w-3 inline mr-1" />
                          {store.contact_phone}
                        </p>
                      )}
                      <Link 
                        to={`/butik/${store.id}`}
                        className="inline-block mt-2 text-sm font-semibold text-[#FF8A00] hover:underline"
                      >
                        Visa butik →
                      </Link>
                    </div>
                  </Popup>
                </Marker>
              )
            ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};
